import * as React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dropdown, Button, Row, MenuProps } from "antd";
import { WithTranslation, withTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { modalProps } from "Pages/Project/QA/utils/modalProps";
import { EditViewFilters, EditViewFiltersProps } from "./EditViewFilters";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores";
import { viewsStore } from "../Stores/ViewsStore";
import { ViewUIFilters } from "../Pages/View";
import { withModal, WithModal } from "Components/General/Modal/withModal";
import { SaveViewFilter, SaveViewFilterProps } from "./SaveViewFilter";
import { notificationSuccess } from "utils/libHelpers/AntdNotification";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

interface SaveFiltersButtonProps extends WithTranslation, WithModal {
  viewId: number;
  activeFilters: ViewUIFilters;
  onFilter: (filters: ViewUIFilters) => void;
  isActive: boolean;
}

interface SaveFiltersButtonState {
  data: API_ViewFilter[];
  loading: boolean;
}

export const SaveFiltersButton = withTranslation(I18N_NS.PROJECT)(
  withModal(
    class extends React.Component<SaveFiltersButtonProps, SaveFiltersButtonState> {
      constructor(props: SaveFiltersButtonProps) {
        super(props);
        this.state = {
          data: [],
          loading: false,
        };
      }

      componentDidMount() {
        this.fetch();
      }

      fetch = async () => {
        try {
          const { viewId } = this.props;
          const response = await API.getData(API_URL.VIEWS.getViewFilters(projectsDataStore.projectId, viewId));
          this.setState({ data: response });
        } catch (error) {
          throw error;
        }
      };

      handleExecuteFilter = ({ key }) => {
        if (key === "save_btn") {
          this.saveCurrentFilter();
        }
      };

      saveCurrentFilter = async () => {
        const { activeFilters, showModal, t } = this.props;
        // dummy check for empty object
        if (!this.disabledFilterCreate) {
          showModal<Partial<SaveViewFilterProps>>(SaveViewFilter, {
            modalProps: { ...modalProps, title: t("Save filter") },
            componentProps: { filterData: activeFilters, onSubmit: this.handleSave },
          });
        }
      };

      handleSave = async (formData: API_ViewFilter) => {
        const { t, viewId } = this.props;
        await API.newData(API_URL.VIEWS.createViewFilter(projectsDataStore.projectId, viewId), formData);
        notificationSuccess(t("Success"), t("View filter settings saved"));
        this.fetch();
      };

      handleEdit = async (formData: API_ViewFilter) => {
        const { t, viewId } = this.props;
        await API.editData(API_URL.VIEWS.updateViewFilter(projectsDataStore.projectId, viewId, formData.id), formData);
        notificationSuccess(t("Success"), t("View filter settings updated"));
        this.fetch();
      };

      // eslint-disable-next-line unused-imports/no-unused-vars
      executeFilter = ({ id, name, ...filterData }: API_ViewFilter) => {
        this.props.onFilter({ ...filterData.metadata, created: filterData.created });
      };

      deleteFilter = async (filterId: number) => {
        const { t, viewId } = this.props;
        await API.deleteData(API_URL.VIEWS.deleteViewFilter(projectsDataStore.projectId, viewId, filterId));
        notificationSuccess(t("Success"), t("View filter deleted"));
        this.fetch();
      };

      get disabledFilterCreate() {
        // dummy check for empty object
        return JSON.stringify(this.props.activeFilters).length === 2;
      }

      get menu(): MenuProps {
        const { t, showModal } = this.props;
        const menuItems: MenuProps["items"] = this.state.data.map((item, idx) => ({
          key: idx,
          label: (
            <Row justify="space-between">
              <Button
                size="small"
                style={{
                  marginRight: "10px",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  maxWidth: 400,
                }}
                onClick={() => this.executeFilter(this.state.data[idx])}
              >
                {item.name}
              </Button>
              <div>
                {
                  <span
                    style={{ marginRight: "10px" }}
                    onClick={() => {
                      showModal<Partial<EditViewFiltersProps>>(EditViewFilters, {
                        modalProps: { ...modalProps, title: t("Edit filters") },
                        componentProps: {
                          initialData: item,
                          metadataDefinitions: viewsStore.currentView.metadata,
                          onSubmit: this.handleEdit,
                        },
                      });
                    }}
                  >
                    <FontAwesomeIcon icon={["fas", "edit"]} size="1x" fixedWidth={true} />
                  </span>
                }
                <span
                  onClick={() => {
                    this.deleteFilter(this.state.data[idx].id);
                  }}
                >
                  <FontAwesomeIcon icon={["fas", "trash"]} size="1x" fixedWidth={true} />
                </span>
              </div>
            </Row>
          ),
        }));

        menuItems.unshift({
          key: "save_btn",
          label: (
            <Button type="primary" disabled={this.disabledFilterCreate} size="small">
              {this.props.t("Save column filter")}
            </Button>
          ),
        });
        return {
          items: menuItems,
          style: { maxHeight: "300px", overflowY: "auto" },
          onClick: this.handleExecuteFilter,
        };
      }
      render() {
        const { isActive } = this.props;
        const icon: IconProp = isActive ? ["fas", "star"] : ["far", "star"];
        return (
          <>
            <Dropdown menu={this.menu} trigger={["click"]}>
              <span style={{ cursor: "pointer", display: "inline-block", padding: 4 }}>
                <FontAwesomeIcon icon={icon} fixedWidth={true} size="lg" />
              </span>
            </Dropdown>
          </>
        );
      }
    }
  )
);
